import React from "react";
import EventStore from '@stores/EventStore';
import { observer } from 'mobx-react';
import { reaction, observable } from 'mobx';
import PageHeader from '@components/PageHeader';
import PageFooter from '@components/PageFooter';
import CustomInput from '@components/CustomInput';
import BillingCreditStore from '@stores/BillingCreditStore';
import utils from '@utils/utils';
import validate from '@utils/validate';

@observer
export default class BillingCreditPage extends React.Component {
    @observable pageState = {
        SSN: {
            value: '',
            valid: false,
            changed: false,
            customSend: function(ssn) {
                BillingCreditStore.sendFormField({
                    field: 'SSN',
                    value: utils.getEncrypt() ? utils.pieProtectSPI(ssn) : ssn
                });
            }
        },
        dateOfBirth: {
            value: '',
            valid: false,
            changed: false,
            formatValue: function(value) {
                return value.replace(/\//g,'');
            }
        },
        pin: {
            value: '',
            valid: false,
            changed: false
        },
        pinConf: {
            value: '',
            valid: false,
            changed: false
        },
        firstName: {
            value: '',
            valid: false,
            changed: false
        },
        middleInitial: {
            value: '',
            valid: true,
            changed: false
        },
        lastName: {
            value: '',
            valid: false,
            changed: false
        },
        suffix: {
            value: '',
            valid: true,
            changed: false
        },
        address1: {
            value: '',
            valid: false,
            changed: false,
            customSend: function(streetAddress) {
                var num = streetAddress.substr(0,streetAddress.indexOf(' ')),
                    name = streetAddress.substr(streetAddress.indexOf(' ')+1);
                BillingCreditStore.sendFormField({
                    field: 'streetNumber',
                    value: num
                });
                BillingCreditStore.sendFormField({
                    field: 'streetName',
                    value: name
                });
            }
        },
        address2: {
            value: '',
            valid: true,
            changed: false,
            customSend: function(num) {
                BillingCreditStore.sendFormField({
                    field: 'apartment',
                    value: num
                });
            }
        },
        idZip: {
            value: '',
            valid: false,
            changed: false
        },
        canBeReached: {
            value: '',
            valid: false,
            changed: false
        },
        emails: {
            value: '',
            valid: false,
            changed: false
        }
    };

    endReaction = reaction(() => EventStore.eventObj, () => {
        if(EventStore.eventObj.event === 'creditAppData') {
            this.pageState[EventStore.eventObj.payload.field].value = EventStore.eventObj.payload.value;
        }
    });

    constructor() {
        super();
        this.showAddress = false;
        this.showDOBSSN = true;

        if(EventStore.eventObj.payload.showAddress === 'true') {
            this.showAddress = true;
        }
        if(EventStore.eventObj.payload.showDOBSSN === 'false') {
            this.showDOBSSN = false;
        }

        this.handleChange = this.handleChange.bind(this);
        this.blurEventHandler = this.blurEventHandler.bind(this);
    }

    handleChange(event, validator) {
        this.pageState[event.target.id].value = event.target.value;
        if(typeof(validator) === 'function') {
            this.pageState[event.target.id].valid = validator();
        }
        this.pageState[event.target.id].changed = true;
    }

    blurEventHandler(event) {
        if(this.pageState[event.target.id].valid === true && this.pageState[event.target.id].changed === true) {
            if(typeof(this.pageState[event.target.id].customSend) === 'function') {
                this.pageState[event.target.id].customSend(this.pageState[event.target.id].formatValue ? this.pageState[event.target.id].formatValue(event.target.value) : event.target.value);
            } else {
                BillingCreditStore.sendFormField({
                    field: event.target.id,
                    value: this.pageState[event.target.id].formatValue ? this.pageState[event.target.id].formatValue(event.target.value) : event.target.value
                });
            }
            this.pageState[event.target.id].changed = false;
        }
    }

    render() {
        return (
            <div className="padding-bot-50">
                <PageHeader title="Billing & Credit Information"/>
                <form onBlur={this.blurEventHandler} autoComplete="off">
                    { this.showAddress === true && 
                        <div className="margin-bot-10">
                            <div className="mob-center">
                                <h3>Billing Information</h3>
                            </div>
                            <div className="padding-left-10">
                                <div className="inline-block margin-bot-15 col">
                                    <label htmlFor="firstName">First Name<abbr title="required">*</abbr></label>
                                    <CustomInput id="firstName" 
                                        value={this.pageState.firstName.value} 
                                        onChange={this.handleChange} 
                                        type="text" 
                                        placeholder="First Name" 
                                        isValid={this.pageState.firstName.valid} 
                                        validator={() => { return validate.isNotEmpty(this.pageState.firstName.value);}} 
                                        required="required" 
                                    />
                                </div>
                                <div className="inline-block margin-bot-15 col">
                                    <label htmlFor="middleInitial">Mid Init.</label>
                                    <CustomInput id="middleInitial" 
                                        value={this.pageState.middleInitial.value} 
                                        onChange={this.handleChange} 
                                        type="text" 
                                        placeholder="Middle Initial" 
                                        maxLength="1" 
                                        isValid={this.pageState.middleInitial.valid} 
                                    />
                                </div>
                                <div className="inline-block margin-bot-15 col">
                                    <label htmlFor="lastName">Last Name<abbr title="required">*</abbr></label>
                                    <CustomInput id="lastName" 
                                        value={this.pageState.lastName.value} 
                                        onChange={this.handleChange} 
                                        type="text" 
                                        placeholder="Last Name" 
                                        isValid={this.pageState.lastName.valid} 
                                        validator={() => { return validate.isNotEmpty(this.pageState.lastName.value);}} 
                                        required="required" 
                                    />
                                </div>
                                <div className="inline-block margin-bot-15 col">
                                    <label htmlFor="suffix">Suffix</label>
                                    <CustomInput id="suffix" 
                                        value={this.pageState.suffix.value} 
                                        onChange={this.handleChange} 
                                        type="text" 
                                        placeholder="Suffix (Ex: Jr, Sr)" 
                                        isValid={this.pageState.suffix.valid} 
                                    />
                                </div>
                                <div className="inline-block margin-bot-15 col">
                                    <label htmlFor="address1">Address Line 1<abbr title="required">*</abbr></label>
                                    <CustomInput id="address1" 
                                        value={this.pageState.address1.value} 
                                        onChange={this.handleChange} 
                                        type="text" 
                                        placeholder="Street Address" 
                                        isValid={this.pageState.address1.valid} 
                                        validator={() => { return validate.isValidStreetAddress(this.pageState.address1.value);}} 
                                        ruleMessage="Please enter valid street address. Ex. 123 Main St" 
                                        required="required" 
                                    />
                                    <span className="sub-input">Street address</span>
                                </div>
                                <div className="inline-block margin-bot-15 col">
                                    <label htmlFor="address2">Address Line 2</label>
                                    <CustomInput id="address2" 
                                        value={this.pageState.address2.value} 
                                        onChange={this.handleChange} 
                                        type="text" 
                                        placeholder="Apartment Number" 
                                        isValid={this.pageState.address2.valid} 
                                    />
                                    <span className="sub-input">Apartment Number</span>
                                </div>
                                <div className="margin-bot-15 col">
                                    <label htmlFor="idZip">Zip Code<abbr title="required">*</abbr></label>
                                    <CustomInput id="idZip" 
                                        value={this.pageState.idZip.value} 
                                        onChange={this.handleChange} 
                                        type="text" 
                                        placeholder="" 
                                        maxLength="5"
                                        isValid={this.pageState.idZip.valid} 
                                        validator={() => { return validate.isValidNumber(this.pageState.idZip.value, 5, 5);}} 
                                        ruleMessage="Please enter 5 digit zip code." 
                                        required="required" 
                                    />
                                </div>
                                <div className="inline-block margin-bot-15 col">
                                    <label htmlFor="canBeReached">Can Be Reached<abbr title="required">*</abbr></label>
                                    <CustomInput id="canBeReached" 
                                        value={this.pageState.canBeReached.value} 
                                        onChange={this.handleChange} 
                                        type="text" 
                                        placeholder="Can Be Reached Number" 
                                        maxLength="10" 
                                        isValid={this.pageState.canBeReached.valid} 
                                        validator={() => { return validate.isValidNumber(this.pageState.canBeReached.value, 10, 10);}} 
                                        ruleMessage="Please enter 10 digit telephone number." 
                                        required="required" 
                                    />
                                </div>
                                <div className="inline-block margin-bot-15 col">
                                    <label htmlFor="emails">Email<abbr title="required">*</abbr></label>
                                    <CustomInput id="emails" 
                                        value={this.pageState.emails.value}
                                        onChange={this.handleChange} 
                                        type="text"
                                        placeholder="Email"
                                        isValid={this.pageState.emails.valid} 
                                        validator={() => { return validate.isValidEmail(this.pageState.emails.value);}} 
                                        ruleMessage="Please enter valid email." 
                                        required="required" 
                                    />
                                </div>
                            </div>
                        </div>
                    }
                    <div className="margin-bot-10">
                        <div className="mob-center">
                            <h3 className="">Credit Information</h3>
                        </div>
                        <div className="padding-left-10">
                            { this.showDOBSSN === true && 
                                <div>
                                    <div className="inline-block margin-bot-15 col">
                                        <label htmlFor="SSN">Social Security Number<abbr title="required">*</abbr></label>
                                        <CustomInput id="SSN" 
                                            placeholder="SSN" 
                                            value={this.pageState.SSN.value} 
                                            onChange={this.handleChange} 
                                            type="text" 
                                            maxLength="9" 
                                            ruleMessage="SSN requires 9 digits." 
                                            isValid={this.pageState.SSN.valid} 
                                            validator={() => { this.pageState.pin.value = ''; this.pageState.pinConf.value = ''; return validate.isValidNumber(this.pageState.SSN.value, 9, 9);}} 
                                            required="required"
                                        />
                                    </div>
                                    <div className="inline-block margin-bot-15 col">
                                        <label htmlFor="dateOfBirth">Date of Birth<abbr title="required">*</abbr></label>
                                        <CustomInput id="dateOfBirth" 
                                            placeholder="MM/DD/YYYY" 
                                            value={this.pageState.dateOfBirth.value} 
                                            onChange={this.handleChange} 
                                            type="text" 
                                            maxLength="10" 
                                            isValid={this.pageState.dateOfBirth.valid} 
                                            validator={() => { return validate.isValidDate(this.pageState.dateOfBirth.value);}} 
                                            ruleMessage="Please enter Date in specified format." 
                                            required="required"
                                        />
                                        <span className="sub-input">MM/DD/YYYY</span>
                                    </div>
                                </div>
                            }
                            <div className="inline-block margin-bot-15 col">
                                <label htmlFor="pin">PIN<abbr title="required">*</abbr> 
                                    <i className="fa fa-info-circle tooltip margin-h-15" aria-hidden="true">
                                        <div className="tooltiptext brandfont">
                                            <ul>
                                                <li>Enter 4 digit Number.</li>
                                                <li>Should not match the sequence of last 4 SSN, both forward and backward.</li>
                                                <li>Should not be all same number.</li>
                                                <li>Should not be sequential, forward and backward.</li>
                                            </ul>
                                        </div>
                                    </i>
                                </label>
                                <CustomInput id="pin" 
                                    placeholder="PIN" 
                                    value={this.pageState.pin.value} 
                                    onChange={this.handleChange} 
                                    type="password" 
                                    maxLength="4" 
                                    ruleMessage="Please enter a valid PIN." 
                                    isValid={this.pageState.pin.valid} 
                                    validator={() => { this.pageState.pinConf.value = ''; return validate.isValidPin(this.pageState.pin.value, this.pageState.SSN.value, 4, 4);}} 
                                    disable={this.pageState.SSN.valid === false && this.showDOBSSN === true} 
                                    required="required" 
                                    autoComplete="off"
                                />
                            </div>
                            <div className="inline-block margin-bot-15 col">
                                <label htmlFor="pinConf">Confirm PIN<abbr title="required">*</abbr></label>
                                <CustomInput id="pinConf" placeholder="PIN" 
                                    value={this.pageState.pinConf.value} 
                                    onChange={this.handleChange} 
                                    type="password" 
                                    maxLength="4" 
                                    ruleMessage="PINs do not match." 
                                    isValid={this.pageState.pinConf.valid} 
                                    validator={() => { return validate.isMatching(this.pageState.pinConf.value, this.pageState.pin.value);}} 
                                    disable={this.pageState.pin.valid === false || (this.pageState.SSN.valid === false && this.showDOBSSN === true)} 
                                    required="required" 
                                    autoComplete="off"
                                />
                            </div>
                        </div>
                    </div>
                </form>
                <PageFooter />
            </div>
        );
    }

    componentDidMount() {
        
    }

    componentWillUnmount() {
        this.endReaction();
    }
}


// WEBPACK FOOTER //
// ./src/js/pages/BillingCreditPage.js